import { z } from 'zod';
import { CompanyRole } from '../enums';
import { GSTIN_REGEX, PAN_REGEX } from '../constants';
import { addressSchema, booleanish, emailSchema, paginationSchema, phoneSchema, zEnum } from './common';

export const companyProfileSchema = z.object({
  name: z.string().trim().min(2, 'Enter the company name').max(200),
  legalName: z.string().trim().max(250).optional().or(z.literal('')),
  industry: z.string().trim().max(120).optional().or(z.literal('')),
  /** Head-count band — shown on the public company page. */
  companySize: z
    .enum(['1-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'])
    .optional(),
  foundedYear: z.coerce.number().int().min(1850).max(new Date().getFullYear()).optional(),
  website: z.string().trim().url('Enter a valid URL').max(255).optional().or(z.literal('')),
  about: z.string().trim().max(4000).optional().or(z.literal('')),
  gstin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(GSTIN_REGEX, 'Enter a valid 15-character GSTIN')
    .optional()
    .or(z.literal('')),
  pan: z
    .string()
    .trim()
    .toUpperCase()
    .regex(PAN_REGEX, 'Enter a valid PAN')
    .optional()
    .or(z.literal('')),
  cin: z.string().trim().max(30).optional().or(z.literal('')),
  registeredAddress: addressSchema,
  contactPersonName: z.string().trim().min(2).max(120),
  contactPersonDesignation: z.string().trim().max(100).optional().or(z.literal('')),
  contactPhone: phoneSchema,
  contactEmail: emailSchema,
  logoKey: z.string().trim().max(500).optional().or(z.literal('')),
});
export type CompanyProfileInput = z.infer<typeof companyProfileSchema>;

/** A factory / plant / site. Vacancies and deployments both hang off this. */
export const factoryLocationSchema = z.object({
  name: z.string().trim().min(2, 'Enter the unit / plant name').max(150),
  code: z.string().trim().max(30).optional().or(z.literal('')),
  address: addressSchema,
  latitude: z.coerce.number().min(-90).max(90).optional(),
  longitude: z.coerce.number().min(-180).max(180).optional(),
  contactPersonName: z.string().trim().max(120).optional().or(z.literal('')),
  contactPhone: phoneSchema.optional(),
  /** Nearest bus stop / railway station — a real deciding factor for workers. */
  transportNote: z.string().trim().max(300).optional().or(z.literal('')),
  isActive: z.boolean().default(true),
});
export type FactoryLocationInput = z.infer<typeof factoryLocationSchema>;

export const departmentSchema = z.object({
  name: z.string().trim().min(2).max(120),
  code: z.string().trim().max(30).optional().or(z.literal('')),
  locationId: z.string().uuid().optional(),
  headName: z.string().trim().max(120).optional().or(z.literal('')),
  isActive: z.boolean().default(true),
});
export type DepartmentInput = z.infer<typeof departmentSchema>;

export const inviteCompanyUserSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  email: emailSchema,
  phone: phoneSchema,
  companyRole: zEnum(CompanyRole).default(CompanyRole.RECRUITER),
});
export type InviteCompanyUserInput = z.infer<typeof inviteCompanyUserSchema>;

export const companyVerificationSchema = z.object({
  approve: z.boolean(),
  remarks: z.string().trim().max(500).optional(),
});
export type CompanyVerificationInput = z.infer<typeof companyVerificationSchema>;

export const blockCompanySchema = z.object({
  isBlocked: z.boolean(),
  reason: z.string().trim().min(5, 'Give a reason').max(500),
});
export type BlockCompanyInput = z.infer<typeof blockCompanySchema>;

/**
 * Public company directory.
 *
 * Note what is deliberately NOT a filter: verification. The directory only ever
 * lists verified, unblocked employers, and that is a floor rather than a
 * checkbox. A platform whose promise to candidates is "every one has been
 * checked by our team" cannot also offer to show them the ones that have not.
 *
 * `hiringNow` IS a filter, because a candidate may reasonably want to see a
 * manufacturer that is not advertising today and check back.
 */
export const companySearchSchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  industry: z.string().trim().max(120).optional(),
  state: z.string().trim().max(100).optional(),
  city: z.string().trim().max(100).optional(),
  hiringNow: booleanish.default(true),
});
export type CompanySearchQuery = z.infer<typeof companySearchSchema>;

/**
 * Dropdown options for the directory, counted so a filter that would return
 * nothing is never offered. Cities are nested under their state so the City
 * dropdown can narrow when a State is picked without a second round trip.
 */
export interface CompanyFacets {
  industries: Array<{ value: string; count: number }>;
  states: Array<{ value: string; count: number; cities: Array<{ value: string; count: number }> }>;
}
